import { c as _c } from "react/compiler-runtime";
import type { ChainId, MarketParams } from '@morpho-org/blue-sdk';
import type { SimulationAuthorization, SimulationLimits } from '@morpho-org/evm-simulation';
import { isDefined } from '@morpho-org/morpho-ts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { TransactionBuilder } from '@/contexts/Transaction/TransactionV2.context';
import { useWeb3Context } from '@/contexts/Web3Context';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { getSimulate, isSimulationUnsupportedSender, parseSimulatedAssetChanges, simulationAuthorizationsKey, toSimulationTx, type Transaction } from '@/services/simulate/simulate';
import { resolveSimulatedFinalPosition, selectUserTokenDelta, type SimulationStatus } from '@/services/simulate/simulatedPosition.helpers';
import { buildSimulationLimits } from '@/services/simulate/simulationLimits';
import { useVerifiedSimulationResult } from '@/services/simulate/useVerifiedSimulationResult';
import { computeLtv } from './marketAction.helpers';
import { buildMarketPositionReadTxs, decodeMarketPositionReads } from './marketPositionReads';

/** Review-time simulation outcome (shared with the Vault V2 flow). */
export type MarketSimulationStatus = SimulationStatus;
const BLACKLIST_ERROR_CODE = 'BLACKLIST_ERROR';
export interface UseMarketSimulatedFinalPositionParams {
  chainId: ChainId;
  marketId: string | undefined;
  /** Target market params — `.id` keys the appended reads and rebuilds the `Market` for decoding. */
  marketParams: MarketParams | undefined;
  /** Builder whose unsigned bundle is simulated. */
  transaction: TransactionBuilder | undefined;
  /** Typed preview authorizations (approvals, permits, Blue authorizations) the SDK prepares before the unsigned bundle. */
  simulationAuthorizations?: readonly SimulationAuthorization[];
  /** Stable query-key identity when calldata churns each poll (e.g. wall-clock maxSharePrice). */
  simulationKey?: ReadonlyArray<unknown>;
  /** Inputs still building → report loading (skeleton) rather than flash the JS-math estimate. */
  isPreparing?: boolean;
  /** Force simulation even when the preview flag is off (transaction-safety invariant). */
  requiresSuccessfulSimulation?: boolean;
  /**
   * `getRequirements` has resolved for the current action. The simulation MUST NOT run until this
   * is true: the preview authorizations (approvals, `setAuthorization`) are derived from the
   * requirements, so simulating early reverts `unauthorized` (and only succeeds on the re-run once
   * the requirements land).
   */
  areRequirementsReady?: boolean;
  /** Current position + oracle price (from `useMarketBlue().positionData`). Price feeds the LTV.
   * `supplyAssets` only matters to the lend-position withdrawal flow; omit it elsewhere. */
  current: {
    collateral: bigint;
    borrowAssets: bigint;
    price: bigint;
    supplyAssets?: bigint;
  } | undefined;
  /** JS-math position-after, shown when the simulation is unavailable/unverified. */
  fallback: {
    collateral: bigint;
    borrowAssets: bigint;
    supplyAssets?: bigint;
  } | undefined;
}

/**
 * Market-actions-v2 sister of `useVaultV2SimulatedFinalPosition`: reads the
 * **post-action** Collateral / Loan straight from the simulated bundle (WS4,
 * "Option B"). It appends `Morpho.position` / `Morpho.market` staticcalls to the
 * unsigned bundle and decodes their `returnData` from the simulation result, so
 * the displayed position is the real on-chain state the tx would produce — not a
 * delta inferred from token balance changes.
 *
 * Falls back to the JS-math position-after with an estimate badge when the
 * simulation is unavailable/unverified or the reads can't be decoded — same UX as
 * Vault V2. LTV is recomputed from the resolved collateral/loan + the pre-fetched
 * oracle price (it doesn't move within a single-block sim). Liq. LTV (`params.lltv`)
 * is static and supplied by the recap, not here.
 */
export function useMarketSimulatedFinalPosition(t0) {
  const $ = _c(84);
  const {
    chainId,
    marketId,
    marketParams,
    transaction,
    simulationAuthorizations,
    simulationKey,
    isPreparing: t1,
    requiresSuccessfulSimulation: t2,
    areRequirementsReady: t3,
    current,
    fallback
  } = t0;
  const isPreparing = t1 === undefined ? false : t1;
  const requiresSuccessfulSimulation = t2 === undefined ? false : t2;
  const areRequirementsReady = t3 === undefined ? true : t3;
  const {
    account
  } = useWeb3Context();
  const userAddress = account.address;
  const {
    evmPreviewSimulation: isSimulationEnabled
  } = useFeatureFlags();
  let t4;
  if ($[0] !== chainId || $[1] !== marketParams || $[2] !== userAddress) {
    t4 = marketParams && userAddress ? buildMarketPositionReadTxs({
      chainId,
      marketParams,
      user: userAddress
    }) : undefined;
    $[0] = chainId;
    $[1] = marketParams;
    $[2] = userAddress;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const readTxs = t4;
  let t5;
  bb0: {
    if (!transaction || !userAddress) {
      let t6;
      if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = {
          transactions: undefined,
          limits: undefined
        };
        $[4] = t6;
      } else {
        t6 = $[4];
      }
      t5 = t6;
      break bb0;
    }
    let finalTx;
    let t6;
    if ($[5] !== transaction || $[6] !== userAddress) {
      const builtTx = transaction.buildTx();
      finalTx = toSimulationTx(userAddress, builtTx);
      t6 = buildSimulationLimits(builtTx.action);
      $[5] = transaction;
      $[6] = userAddress;
      $[7] = finalTx;
      $[8] = t6;
    } else {
      finalTx = $[7];
      t6 = $[8];
    }
    const limits = t6;
    let t7;
    if ($[9] !== finalTx || $[10] !== readTxs) {
      t7 = readTxs ? [finalTx, ...readTxs] : [finalTx];
      $[9] = finalTx;
      $[10] = readTxs;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    let t8;
    if ($[12] !== limits || $[13] !== t7) {
      t8 = {
        transactions: t7,
        limits
      };
      $[12] = limits;
      $[13] = t7;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    t5 = t8;
  }
  const {
    transactions,
    limits: limits_0
  } = t5;
  let t6;
  if ($[15] !== account || $[16] !== chainId || $[17] !== isSimulationEnabled || $[18] !== requiresSuccessfulSimulation || $[19] !== userAddress) {
    t6 = (isSimulationEnabled || requiresSuccessfulSimulation) && isDefined(userAddress) && !account.isWrongChain && !isSimulationUnsupportedSender(chainId, account);
    $[15] = account;
    $[16] = chainId;
    $[17] = isSimulationEnabled;
    $[18] = requiresSuccessfulSimulation;
    $[19] = userAddress;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const isSimulationIntended = t6;
  let t7;
  if ($[21] !== areRequirementsReady || $[22] !== isPreparing || $[23] !== isSimulationIntended || $[24] !== transactions) {
    t7 = isSimulationIntended && !isPreparing && areRequirementsReady && isDefined(transactions);
    $[21] = areRequirementsReady;
    $[22] = isPreparing;
    $[23] = isSimulationIntended;
    $[24] = transactions;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const canFetch = t7;
  let t8;
  if ($[26] !== simulationAuthorizations) {
    t8 = simulationAuthorizationsKey(simulationAuthorizations);
    $[26] = simulationAuthorizations;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const prefixSignature = t8;
  let t9;
  if ($[28] !== simulationKey || $[29] !== transactions) {
    t9 = simulationKey ?? transactions?.at(-1);
    $[28] = simulationKey;
    $[29] = transactions;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== chainId || $[32] !== marketId || $[33] !== prefixSignature || $[34] !== t9 || $[35] !== userAddress) {
    t10 = ["market-simulated-final-position", chainId, userAddress, marketId, t9, prefixSignature];
    $[31] = chainId;
    $[32] = marketId;
    $[33] = prefixSignature;
    $[34] = t9;
    $[35] = userAddress;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  let t11;
  if ($[37] !== chainId || $[38] !== limits_0 || $[39] !== simulationAuthorizations || $[40] !== transactions) {
    t11 = () => getSimulate({
      chainId,
      transactions,
      mode: "preview",
      authorizations: simulationAuthorizations,
      limits: limits_0
    });
    $[37] = chainId;
    $[38] = limits_0;
    $[39] = simulationAuthorizations;
    $[40] = transactions;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  let t12;
  if ($[42] !== canFetch || $[43] !== t10 || $[44] !== t11) {
    t12 = {
      queryKey: t10,
      queryFn: t11,
      enabled: canFetch,
      retry: false,
      staleTime: 15000,
      gcTime: 60000,
      placeholderData: keepPreviousData
    };
    $[42] = canFetch;
    $[43] = t10;
    $[44] = t11;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  const {
    data,
    error,
    isPlaceholderData,
    isFetching
  } = useQuery(t12);
  const isSettled = !isPreparing && areRequirementsReady;
  let t13;
  if ($[46] !== data || $[47] !== error || $[48] !== isSettled) {
    t13 = {
      data,
      error,
      isSettled
    };
    $[46] = data;
    $[47] = error;
    $[48] = isSettled;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  const {
    hasVerifiedResult,
    hasSettledResult
  } = useVerifiedSimulationResult(t13);
  let t14;
  bb1: {
    if (!isSimulationIntended) {
      t14 = "disabled";
      break bb1;
    }
    if (hasVerifiedResult && hasSettledResult) {
      t14 = "success";
      break bb1;
    }
    if (!isSettled) {
      t14 = "loading";
      break bb1;
    }
    if (!isDefined(transactions)) {
      t14 = "disabled";
      break bb1;
    }
    if (isPlaceholderData) {
      t14 = "loading";
      break bb1;
    }
    if (error) {
      t14 = "error";
      break bb1;
    }
    if (data?.code === BLACKLIST_ERROR_CODE) {
      t14 = "blacklist";
      break bb1;
    }
    if (data) {
      t14 = "error";
      break bb1;
    }
    t14 = "loading";
  }
  const status = t14;
  const isRefreshing = isSimulationIntended && (!isSettled || isFetching || isPlaceholderData);
  let t15;
  bb2: {
    if (!data?.success || !marketParams || !readTxs || !transactions) {
      t15 = undefined;
      break bb2;
    }
    let t16;
    if ($[50] !== data.calls || $[51] !== marketParams || $[52] !== transactions.length) {
      t16 = decodeMarketPositionReads({
        calls: data.calls,
        expectedCallCount: transactions.length,
        marketParams
      });
      $[50] = data.calls;
      $[51] = marketParams;
      $[52] = transactions.length;
      $[53] = t16;
    } else {
      t16 = $[53];
    }
    t15 = t16;
  }
  const simulated = t15;
  let t16;
  bb3: {
    if (status !== "success" || !data?.success || !marketParams || !userAddress) {
      t16 = undefined;
      break bb3;
    }
    let t17;
    if ($[54] !== data.assetChanges || $[55] !== marketParams.loanToken || $[56] !== userAddress) {
      const assetChanges = parseSimulatedAssetChanges(data.assetChanges);
      t17 = selectUserTokenDelta(assetChanges, userAddress, marketParams.loanToken);
      $[54] = data.assetChanges;
      $[55] = marketParams.loanToken;
      $[56] = userAddress;
      $[57] = t17;
    } else {
      t17 = $[57];
    }
    t16 = t17;
  }
  const loanAssetDelta = t16;
  const t17 = simulated?.collateral;
  const t18 = fallback?.collateral;
  let t19;
  if ($[58] !== status || $[59] !== t17 || $[60] !== t18) {
    t19 = resolveSimulatedFinalPosition({
      status,
      simulatedValue: t17,
      fallbackPosition: t18
    });
    $[58] = status;
    $[59] = t17;
    $[60] = t18;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  const collateral = t19;
  const t20 = simulated?.borrowAssets;
  const t21 = fallback?.borrowAssets;
  let t22;
  if ($[62] !== status || $[63] !== t20 || $[64] !== t21) {
    t22 = resolveSimulatedFinalPosition({
      status,
      simulatedValue: t20,
      fallbackPosition: t21
    });
    $[62] = status;
    $[63] = t20;
    $[64] = t21;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  const loan = t22;
  const t23 = simulated?.supplyAssets;
  const t24 = fallback?.supplyAssets;
  let t25;
  if ($[66] !== status || $[67] !== t23 || $[68] !== t24) {
    t25 = resolveSimulatedFinalPosition({
      status,
      simulatedValue: t23,
      fallbackPosition: t24
    });
    $[66] = status;
    $[67] = t23;
    $[68] = t24;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  const supply = t25;
  let t26;
  bb4: {
    if (!isDefined(collateral.displayedFinalPosition) || !isDefined(loan.displayedFinalPosition) || !current) {
      t26 = undefined;
      break bb4;
    }
    let t27;
    if ($[70] !== collateral.displayedFinalPosition || $[71] !== current.price || $[72] !== loan.displayedFinalPosition) {
      t27 = computeLtv({
        collateral: collateral.displayedFinalPosition,
        borrowAssets: loan.displayedFinalPosition,
        price: current.price
      });
      $[70] = collateral.displayedFinalPosition;
      $[71] = current.price;
      $[72] = loan.displayedFinalPosition;
      $[73] = t27;
    } else {
      t27 = $[73];
    }
    t26 = t27;
  }
  const ltv = t26;
  const t27 = status === "loading";
  let t28;
  if ($[74] !== collateral.displayedFinalPosition || $[75] !== collateral.isUnverifiedFinalPosition || $[76] !== isRefreshing || $[77] !== loan.displayedFinalPosition || $[78] !== loanAssetDelta || $[79] !== ltv || $[80] !== status || $[81] !== supply.displayedFinalPosition || $[82] !== t27) {
    t28 = {
      collateralAfter: collateral.displayedFinalPosition,
      borrowAfter: loan.displayedFinalPosition,
      supplyAfter: supply.displayedFinalPosition,
      loanAssetDelta,
      ltvAfter: ltv,
      isUnverifiedFinalPosition: collateral.isUnverifiedFinalPosition,
      status,
      isLoading: t27,
      isRefreshing
    };
    $[74] = collateral.displayedFinalPosition;
    $[75] = collateral.isUnverifiedFinalPosition;
    $[76] = isRefreshing;
    $[77] = loan.displayedFinalPosition;
    $[78] = loanAssetDelta;
    $[79] = ltv;
    $[80] = status;
    $[81] = supply.displayedFinalPosition;
    $[82] = t27;
    $[83] = t28;
  } else {
    t28 = $[83];
  }
  return t28;
}